/**
 * Defense-in-depth for **external** redirects (PSP checkout).
 *
 * Backend is trusted, but we never assign `window.location` without validation:
 *  - block `javascript:` / `data:` / `vbscript:`;
 *  - payment redirects require HTTPS + known PSP host (allowlist).
 */

import { markPspReturnPending, type PspReturnContext } from '@/shared/lib/pspReturn'

const DEFAULT_PSP_HOST_SUFFIXES = [
  'checkout.stripe.com',
  'billing.stripe.com',
  'stripe.com',
  'stancer.com',
  'paypal.com',
  'paypalobjects.com',
  'braintreegateway.com',
] as const

function extraPspHostSuffixes(): string[] {
  const raw = import.meta.env.VITE_PSP_CHECKOUT_HOST_ALLOWLIST
  if (typeof raw !== 'string' || raw.trim() === '') {
    return import.meta.env.DEV || import.meta.env.MODE === 'test' ? ['checkout.fake.test'] : []
  }

  return raw
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter((h) => h.length > 0)
}

function hostMatchesSuffix(hostname: string, suffix: string): boolean {
  const host = hostname.toLowerCase()
  const needle = suffix.toLowerCase()
  return host === needle || host.endsWith(`.${needle}`)
}

/** Absolute HTTPS URL (any host). Prefer `isAllowedPspCheckoutUrl` for payment redirects. */
export function isSafeExternalUrl(url: string | null | undefined): url is string {
  if (typeof url !== 'string' || url === '') {
    return false
  }
  try {
    return new URL(url).protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * HTTPS checkout URL whose host is on the PSP allowlist (Stripe / Stancer / PayPal + env extras).
 * Blocks open redirects to attacker-controlled HTTPS hosts after cookie/session theft XSS.
 */
export function isAllowedPspCheckoutUrl(url: string | null | undefined): url is string {
  if (!isSafeExternalUrl(url)) {
    return false
  }
  try {
    const { hostname } = new URL(url)
    const allow = [...DEFAULT_PSP_HOST_SUFFIXES, ...extraPspHostSuffixes()]
    return allow.some((suffix) => hostMatchesSuffix(hostname, suffix))
  } catch {
    return false
  }
}

/** Redirect only when URL is an allowlisted PSP HTTPS URL; otherwise no-op. */
export function safePspCheckoutRedirect(
  url: string | null | undefined,
  context?: PspReturnContext,
): void {
  if (typeof window === 'undefined' || !isAllowedPspCheckoutUrl(url)) {
    return
  }

  const returnPath = `${window.location.pathname}${window.location.search}`
  markPspReturnPending(
    context ?? {
      module: 'checkout',
      returnPath,
    },
  )
  window.location.assign(url)
}

/** @deprecated Prefer `safePspCheckoutRedirect` for payment flows. */
export function safeExternalRedirect(url: string | null | undefined): void {
  if (typeof window === 'undefined' || !isSafeExternalUrl(url)) {
    return
  }
  window.location.assign(url)
}

/**
 * Navigation from a signed `Location` header (PDF): same-origin relative path OR absolute https
 * (http only in non-production local). Blocks script schemes.
 */
export function isSafeNavigationUrl(url: string | null | undefined): url is string {
  if (typeof url !== 'string' || url === '') {
    return false
  }
  if (url.startsWith('/')) {
    return !url.startsWith('//') && !url.startsWith('/\\')
  }
  try {
    const protocol = new URL(url).protocol
    return protocol === 'https:' || (protocol === 'http:' && !import.meta.env.PROD)
  } catch {
    return false
  }
}

/** Navigate only if URL passes `isSafeNavigationUrl`; otherwise no-op. */
export function safeNavigate(url: string | null | undefined): void {
  if (typeof window === 'undefined' || !isSafeNavigationUrl(url)) {
    return
  }
  window.location.assign(url)
}

const COMMAND_PALETTE_PATH_PREFIXES = [
  '/reservation',
  '/ticketing',
  '/lottery',
  '/match-event',
  '/me',
  '/partners',
  '/partner-offers',
  '/listings',
  '/group-trips',
  '/blog',
  '/faq',
  '/legal',
] as const

/** Allowlist for unified-search hit URLs before in-app navigation (⌘K). */
export function isAllowedCommandPalettePath(url: string | null | undefined): url is string {
  if (!isSafeNavigationUrl(url)) {
    return false
  }
  if (url === '/') {
    return true
  }

  return COMMAND_PALETTE_PATH_PREFIXES.some(
    (prefix) => url === prefix || url.startsWith(`${prefix}/`),
  )
}
